import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Gem, Heart, Sun, Moon, Coins, Shield, Lightbulb, Zap, Sparkles, BookOpen, Droplets, Battery, Target, Circle, ChevronDown, ChevronUp, Waves, Eye, Brain, AlertTriangle, PenLine, Camera, Dumbbell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { crystals, remedyCategories, crystalCareGuide, scienceSection, chakras, chakraHealingGuide, healingVibrations, moonCeremonies, psychologicalFramework, crystalSafetyGuide, journalingPrompts, crystalExercises, Crystal, Chakra } from '@/data/crystalData';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { CrystalIdentifier } from '@/components/crystals/CrystalIdentifier';
import { CrystalCard } from '@/components/crystals/CrystalCard';
import { CrystalCollection } from '@/components/crystals/CrystalCollection';
import { CrystalLearningHub } from '@/components/crystals/CrystalLearningHub';
import { Library, GraduationCap } from 'lucide-react';

const psyIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Target,
  Sparkles,
  Shield,
  Eye,
  BookOpen,
  Heart
};
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Heart,
  Sun,
  Moon,
  Coins,
  Shield,
  Lightbulb,
  Zap,
  Eye
};

const RockRemedies = () => {
  const navigate = useNavigate();
  const { goBack } = useNavigationHistory();
  const [selectedCrystal, setSelectedCrystal] = useState<Crystal | null>(null);
  const [activeTab, setActiveTab] = useState('remedies');
  const [expandedChakra, setExpandedChakra] = useState<string | null>(null);

  const getCrystalById = (id: string) => crystals.find(c => c.id === id);

  const getCrystalReason = (crystal: Crystal | undefined, categoryId: string): string | undefined => {
    if (!crystal?.categoryReasons) return undefined;
    return crystal.categoryReasons[categoryId];
  };

  const getCrystalTagline = (crystal: Crystal | undefined, categoryId: string): string | undefined => {
    if (!crystal?.categoryTaglines) return undefined;
    return crystal.categoryTaglines[categoryId];
  };

  const renderCrystalCard = (crystal: Crystal) => (
    <CrystalCard 
      key={crystal.id}
      crystal={crystal}
      onClick={() => setSelectedCrystal(crystal)}
      variant="card"
    />
  );

  const renderCrystalDetail = (crystal: Crystal) => (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm overflow-y-auto">
      <div className="container max-w-2xl mx-auto px-4 py-6">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => setSelectedCrystal(null)}
          className="mb-4"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        
        <CrystalCard 
          crystal={crystal}
          onClick={() => {}}
          variant="detail"
        />

        <h1 className="text-2xl font-semibold text-primary text-center mb-2">{crystal.name}</h1>
        
        <div className="flex flex-wrap gap-2 justify-center mb-6">
          {crystal.chakras.map(chakra => (
            <Badge key={chakra} className="bg-accent/30 text-primary border-0">{chakra}</Badge>
          ))}
          <Badge className="bg-muted/40 text-primary border-accent/30">{crystal.element}</Badge>
        </div>

        <p className="text-primary/90 text-center mb-8 text-base">{crystal.description}</p>

        <div className="space-y-6">
          {/* Properties */}
          <Card className="bg-accent/20 border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-primary font-semibold">
                <Sparkles className="h-5 w-5 text-primary" />
                Properties
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {crystal.properties.map((prop, i) => (
                  <li key={i} className="flex items-start gap-2 text-primary/90 text-base">
                    <span className="text-primary mt-1">•</span>
                    {prop}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Zodiac */}
          <Card className="bg-muted/30 border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-primary font-semibold">Zodiac Signs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {crystal.zodiacSigns.map(sign => (
                  <Badge key={sign} className="bg-accent/20 text-primary border-0">{sign}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Care */}
          <Card className="bg-muted/30 border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-primary font-semibold">
                <Droplets className="h-5 w-5 text-primary" />
                Care Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-primary/90 text-base">{crystal.careInstructions}</p>
              
              <div>
                <p className="font-semibold text-sm text-primary mb-2">Cleansing Methods:</p>
                <div className="flex flex-wrap gap-1">
                  {crystal.cleansingMethods.map(method => (
                    <Badge key={method} className="text-xs bg-accent/20 text-primary border-0">{method}</Badge>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-semibold text-sm text-primary mb-2">Charging Methods:</p>
                <div className="flex flex-wrap gap-1">
                  {crystal.chargingMethods.map(method => (
                    <Badge key={method} className="text-xs bg-muted/40 text-primary border-0">{method}</Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Science */}
          <Card className="bg-primary/10 border-primary/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-primary" />
                The Science
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground/80">{crystal.scientificFacts}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-accent/5 to-secondary/10">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border/50">
        <div className="container max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={goBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Gem className="h-6 w-6 text-[hsl(182,35%,25%)]" />
              <h1 className="text-xl font-medium text-[hsl(182,35%,25%)]">
                Rock Remedies<sup className="text-[0.5em] align-super">®</sup>
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="container max-w-4xl mx-auto px-4 py-6">
        {/* Intro */}
        <Card className="mb-6 bg-gradient-to-br from-accent/20 to-secondary/20 border-accent/30">
          <CardContent className="p-6 text-center">
            <Gem className="h-12 w-12 mx-auto mb-4 text-accent" />
            <h2 className="text-lg font-medium text-foreground mb-2">
              Crystal Wellness for Modern Life
            </h2>
            <p className="text-foreground/70">
              Discover the ancient wisdom and modern science of healing crystals. 
              From the piezoelectric properties that power our technology to time-honoured 
              practices for wellbeing, explore how crystals can enhance your daily life.
            </p>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <ScrollArea className="w-full">
            <TabsList className="inline-flex w-auto min-w-full bg-muted/50 p-1">
              <TabsTrigger value="remedies" className="text-xs px-3">
                <Heart className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Remedies</span>
              </TabsTrigger>
              <TabsTrigger value="identify" className="text-xs px-3">
                <Camera className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Identify</span>
              </TabsTrigger>
              <TabsTrigger value="collection" className="text-xs px-3">
                <Library className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Collection</span>
              </TabsTrigger>
              <TabsTrigger value="learn" className="text-xs px-3">
                <GraduationCap className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Learn</span>
              </TabsTrigger>
              <TabsTrigger value="exercises" className="text-xs px-3">
                <Dumbbell className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Exercises</span>
              </TabsTrigger>
              <TabsTrigger value="psychology" className="text-xs px-3">
                <Brain className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Psychology</span>
              </TabsTrigger>
              <TabsTrigger value="safety" className="text-xs px-3">
                <AlertTriangle className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Safety</span>
              </TabsTrigger>
              <TabsTrigger value="journal" className="text-xs px-3">
                <PenLine className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Journal</span>
              </TabsTrigger>
              <TabsTrigger value="moon" className="text-xs px-3">
                <Moon className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Moon</span>
              </TabsTrigger>
              <TabsTrigger value="vibrations" className="text-xs px-3">
                <Waves className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Vibrations</span>
              </TabsTrigger>
              <TabsTrigger value="chakras" className="text-xs px-3">
                <Circle className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Chakras</span>
              </TabsTrigger>
              <TabsTrigger value="crystals" className="text-xs px-3">
                <Gem className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Crystals</span>
              </TabsTrigger>
              <TabsTrigger value="care" className="text-xs px-3">
                <Droplets className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Care</span>
              </TabsTrigger>
              <TabsTrigger value="science" className="text-xs px-3">
                <Lightbulb className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Science</span>
              </TabsTrigger>
            </TabsList>
          </ScrollArea>

          {/* Remedies Tab */}
          <TabsContent value="remedies" className="space-y-4">
            <p className="text-foreground/70 text-center mb-6">
              Discover crystals curated for specific wellness goals
            </p>
            <div className="grid grid-cols-1 gap-6">
              {remedyCategories.map(category => {
                const IconComponent = iconMap[category.icon] || Heart;
                return (
                  <Card 
                    key={category.id}
                    className={`${category.colour} border-transparent hover:shadow-lg transition-all`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-background/50">
                          <IconComponent className="h-5 w-5 text-foreground" />
                        </div>
                        <CardTitle className="text-base">{category.name}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-foreground/70 mb-4">{category.description}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {category.crystals.map(crystalId => {
                          const crystal = getCrystalById(crystalId);
                          if (!crystal) return null;
                          const tagline = getCrystalTagline(crystal, category.id);
                          return (
                            <CrystalCard 
                              key={crystal.id}
                              crystal={crystal}
                              onClick={() => setSelectedCrystal(crystal)}
                              variant="grid"
                              tagline={tagline}
                              showChakras={false}
                            />
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Identify Tab */}
          <TabsContent value="identify" className="space-y-6">
            <CrystalIdentifier />
          </TabsContent>

          {/* Collection Tab */}
          <TabsContent value="collection" className="space-y-6">
            <CrystalCollection />
          </TabsContent>

          {/* Learn Tab */}
          <TabsContent value="learn" className="space-y-6">
            <CrystalLearningHub onExit={() => setActiveTab('remedies')} />
          </TabsContent>

          {/* Exercises Tab */}
          <TabsContent value="exercises" className="space-y-6">
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Dumbbell className="h-5 w-5 text-primary" />
                  {crystalExercises.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80">{crystalExercises.introduction}</p>
              </CardContent>
            </Card>

            {crystalExercises.exercises.map((exercise) => (
              <Card key={exercise.id} className="bg-muted/30 border-muted/50">
                <CardHeader>
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <CardTitle className="text-lg">{exercise.name}</CardTitle>
                    <div className="flex gap-2">
                      <Badge variant="outline">{exercise.duration}</Badge>
                      <Badge variant="secondary">{exercise.difficulty}</Badge>
                    </div>
                  </div>
                  <CardDescription>{exercise.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {exercise.crystals.map((crystal, i) => (
                      <Badge key={i} className="bg-accent/20 text-accent">{crystal}</Badge>
                    ))}
                  </div>
                  {/* Intention Setting */}
                  <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
                    <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      Set Your Intention
                    </h4>
                    <p className="text-sm text-foreground/80">
                      Before beginning, take a moment to set a clear intention for this exercise. 
                      What do you hope to release, receive, or understand? Hold this intention gently as you proceed.
                    </p>
                  </div>

                  <div className="p-4 bg-background/50 rounded-lg">
                    <h4 className="font-medium text-foreground mb-3">Steps</h4>
                    <ol className="space-y-2">
                      {exercise.steps.map((step, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium text-primary">{i + 1}</span>
                          <span className="text-foreground/80">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                  <div className="p-3 bg-accent/10 rounded-lg">
                    <p className="text-sm text-foreground/70">
                      <strong className="text-foreground/90">Therapeutic Note:</strong> {exercise.therapyNote}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Vibrations Tab */}
          <TabsContent value="vibrations" className="space-y-6">
            <Card className="bg-gradient-to-br from-secondary/20 to-primary/10 border-secondary/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Waves className="h-5 w-5 text-secondary" />
                  {healingVibrations.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 mb-6">{healingVibrations.introduction}</p>
                
                <div className="space-y-4">
                  {healingVibrations.sections.map((section, i) => (
                    <div key={i} className="p-4 bg-background/50 rounded-lg">
                      <h3 className="font-medium text-foreground mb-2">{section.title}</h3>
                      <p className="text-sm text-foreground/70">{section.content}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Crystal Frequencies */}
            <Card className="bg-muted/30 border-muted/50">
              <CardHeader>
                <CardTitle className="text-base">Crystal Vibration Frequencies</CardTitle>
                <CardDescription>Each crystal vibrates at its own unique frequency</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {crystals.filter(c => c.vibrationFrequency).slice(0, 9).map(crystal => (
                    <CrystalCard
                      key={crystal.id}
                      crystal={crystal}
                      onClick={() => setSelectedCrystal(crystal)}
                      variant="inline"
                      showVibration={true}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Moon Ceremonies Tab */}
          <TabsContent value="moon" className="space-y-6">
            {/* Intro */}
            <Card className="bg-gradient-to-br from-primary/10 to-muted/10 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Moon className="h-5 w-5 text-primary" />
                  {moonCeremonies.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80">{moonCeremonies.introduction}</p>
              </CardContent>
            </Card>

            {/* New Moon */}
            <Card className="bg-background/30 border-border/30">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-muted/30 flex items-center justify-center">
                    <Moon className="h-5 w-5 text-muted" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{moonCeremonies.newMoon.title}</CardTitle>
                    <CardDescription className="text-foreground/60">{moonCeremonies.newMoon.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* New Moon Crystals */}
                <div>
                  <p className="font-medium text-sm text-foreground mb-3">Crystals for New Moon Work</p>
                  <div className="grid grid-cols-2 gap-3">
                    {moonCeremonies.newMoon.crystals.map(crystalId => {
                      const crystal = getCrystalById(crystalId);
                      if (!crystal) return null;
                      const reason = moonCeremonies.newMoon.crystalReasons[crystalId as keyof typeof moonCeremonies.newMoon.crystalReasons];
                      return (
                        <CrystalCard
                          key={crystalId}
                          crystal={crystal}
                          onClick={() => setSelectedCrystal(crystal)}
                          variant="inline"
                          reason={reason}
                        />
                      );
                    })}
                  </div>
                </div>

                {/* Steps */}
                <div className="p-4 bg-background/20 rounded-lg">
                  <p className="font-medium text-sm text-foreground mb-3">Ceremony Steps</p>
                  <ol className="space-y-2">
                    {moonCeremonies.newMoon.steps.map((step, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-foreground/80">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-xs text-primary font-medium">
                          {i + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Affirmation */}
                <div className="p-4 bg-primary/10 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground mb-2">New Moon Affirmation</p>
                  <p className="text-sm text-foreground italic">"{moonCeremonies.newMoon.affirmation}"</p>
                </div>
              </CardContent>
            </Card>

            {/* Full Moon */}
            <Card className="bg-secondary/10 border-secondary/20">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                    <Moon className="h-5 w-5 text-secondary" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{moonCeremonies.fullMoon.title}</CardTitle>
                    <CardDescription className="text-foreground/60">{moonCeremonies.fullMoon.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Full Moon Crystals */}
                <div>
                  <p className="font-medium text-sm text-foreground mb-3">Crystals for Full Moon Work</p>
                  <div className="grid grid-cols-2 gap-3">
                    {moonCeremonies.fullMoon.crystals.map(crystalId => {
                      const crystal = getCrystalById(crystalId);
                      if (!crystal) return null;
                      const reason = moonCeremonies.fullMoon.crystalReasons[crystalId as keyof typeof moonCeremonies.fullMoon.crystalReasons];
                      return (
                        <CrystalCard
                          key={crystalId}
                          crystal={crystal}
                          onClick={() => setSelectedCrystal(crystal)}
                          variant="inline"
                          reason={reason}
                        />
                      );
                    })}
                  </div>
                </div>

                {/* Steps */}
                <div className="p-4 bg-background/20 rounded-lg">
                  <p className="font-medium text-sm text-foreground mb-3">Ceremony Steps</p>
                  <ol className="space-y-2">
                    {moonCeremonies.fullMoon.steps.map((step, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-foreground/80">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-secondary/20 flex items-center justify-center text-xs text-secondary font-medium">
                          {i + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Affirmation */}
                <div className="p-4 bg-secondary/10 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground mb-2">Full Moon Affirmation</p>
                  <p className="text-sm text-foreground italic">"{moonCeremonies.fullMoon.affirmation}"</p>
                </div>
              </CardContent>
            </Card>

            {/* Lunar Tips */}
            <Card className="bg-muted/30 border-muted/50">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-accent" />
                  Lunar Tips
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {moonCeremonies.lunarTips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                      <span className="text-accent mt-1">✦</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Chakras Tab */}
          <TabsContent value="chakras" className="space-y-6">
            {/* Intro */}
            <Card className="bg-gradient-to-br from-accent/10 to-secondary/10 border-accent/20">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Circle className="h-5 w-5 text-accent" />
                  {chakraHealingGuide.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 mb-4">{chakraHealingGuide.introduction}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {chakraHealingGuide.howToUse.map((method, i) => (
                    <div key={i} className="p-3 bg-background/50 rounded-lg">
                      <p className="font-medium text-foreground text-sm mb-1">{method.method}</p>
                      <p className="text-xs text-foreground/70">{method.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Chakra Cards */}
            <div className="space-y-3">
              {chakras.map((chakra) => (
                <Collapsible 
                  key={chakra.id} 
                  open={expandedChakra === chakra.id}
                  onOpenChange={(open) => setExpandedChakra(open ? chakra.id : null)}
                >
                  <Card className={`${chakra.colourClass}/10 border-${chakra.colourClass.replace('bg-', '')}/20 transition-all`}>
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-muted/20 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full ${chakra.colourClass} flex items-center justify-center`}>
                              <Circle className="h-4 w-4 text-white" />
                            </div>
                            <div>
                              <CardTitle className="text-base">{chakra.name}</CardTitle>
                              <CardDescription className="text-xs">
                                {chakra.sanskritName} • {chakra.location}
                              </CardDescription>
                            </div>
                          </div>
                          {expandedChakra === chakra.id ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                      <CardContent className="pt-0 space-y-4">
                        {/* Quick Info */}
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline">{chakra.colour}</Badge>
                          <Badge variant="outline">{chakra.element}</Badge>
                        </div>

                        {/* Governs */}
                        <div>
                          <p className="font-medium text-sm text-foreground mb-2">Governs</p>
                          <div className="flex flex-wrap gap-1">
                            {chakra.governs.map(item => (
                              <Badge key={item} variant="secondary" className="text-xs">{item}</Badge>
                            ))}
                          </div>
                        </div>

                        {/* Physical Associations */}
                        <div>
                          <p className="font-medium text-sm text-foreground mb-2">Physical Associations</p>
                          <p className="text-sm text-foreground/70">{chakra.physicalAssociations.join(', ')}</p>
                        </div>

                        {/* Signs of Balance */}
                        <div className="p-3 bg-secondary/10 rounded-lg">
                          <p className="font-medium text-sm text-foreground mb-2">Signs of Balance</p>
                          <ul className="space-y-1">
                            {chakra.signsOfBalance.map((sign, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                                <span className="text-secondary mt-1">✓</span>
                                {sign}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Signs of Imbalance */}
                        <div className="p-3 bg-destructive/10 rounded-lg">
                          <p className="font-medium text-sm text-foreground mb-2">Signs of Imbalance</p>
                          <ul className="space-y-1">
                            {chakra.signsOfImbalance.map((sign, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                                <span className="text-destructive mt-1">•</span>
                                {sign}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Crystals for this Chakra */}
                        <div>
                          <p className="font-medium text-sm text-foreground mb-2">Healing Crystals</p>
                          <div className="flex gap-2 overflow-x-auto pb-2">
                            {chakra.crystals.map(crystalId => {
                              const crystal = getCrystalById(crystalId);
                              if (!crystal) return null;
                              return (
                                <CrystalCard 
                                  key={crystal.id}
                                  crystal={crystal}
                                  onClick={() => setSelectedCrystal(crystal)}
                                  variant="mini"
                                  showChakras={false}
                                />
                              );
                            })}
                          </div>
                        </div>

                        {/* Healing Practices */}
                        <div>
                          <p className="font-medium text-sm text-foreground mb-2">Healing Practices</p>
                          <ul className="space-y-1">
                            {chakra.healingPractices.map((practice, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                                <span className="text-accent mt-1">→</span>
                                {practice}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Affirmation */}
                        <div className="p-4 bg-accent/10 rounded-lg text-center">
                          <p className="text-xs text-muted-foreground mb-1">Affirmation</p>
                          <p className="text-foreground italic">"{chakra.affirmation}"</p>
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              ))}
            </div>
          </TabsContent>

          {/* Crystals Tab */}
          <TabsContent value="crystals" className="space-y-4">
            <p className="text-foreground/70 text-center mb-6">
              Explore our crystal collection
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {crystals.map(renderCrystalCard)}
            </div>
          </TabsContent>

          {/* Care Tab */}
          <TabsContent value="care" className="space-y-6">
            {/* Cleansing */}
            <Card className="bg-accent/10 border-accent/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Droplets className="h-5 w-5 text-accent" />
                  {crystalCareGuide.cleansing.title}
                </CardTitle>
                <CardDescription>{crystalCareGuide.cleansing.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {crystalCareGuide.cleansing.methods.map((method, i) => (
                  <div key={i} className="p-3 bg-background/50 rounded-lg">
                    <p className="font-medium text-foreground mb-1">{method.name}</p>
                    <p className="text-sm text-foreground/70 mb-2">{method.description}</p>
                    <Badge variant="outline" className="text-xs">{method.safeFor}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Charging */}
            <Card className="bg-secondary/10 border-secondary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Battery className="h-5 w-5 text-secondary" />
                  {crystalCareGuide.charging.title}
                </CardTitle>
                <CardDescription>{crystalCareGuide.charging.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {crystalCareGuide.charging.methods.map((method, i) => (
                  <div key={i} className="p-3 bg-background/50 rounded-lg">
                    <p className="font-medium text-foreground mb-1">{method.name}</p>
                    <p className="text-sm text-foreground/70 mb-2">{method.description}</p>
                    <Badge variant="outline" className="text-xs">{method.safeFor}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Intention Setting */}
            <Card className="bg-primary/10 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  {crystalCareGuide.intentionSetting.title}
                </CardTitle>
                <CardDescription>{crystalCareGuide.intentionSetting.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ol className="space-y-3">
                  {crystalCareGuide.intentionSetting.steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-sm font-medium text-primary">
                        {i + 1}
                      </span>
                      <span className="text-foreground/80 pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Science Tab */}
          <TabsContent value="science" className="space-y-6">
            <Card className="bg-muted/30 border-muted/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-primary" />
                  {scienceSection.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80 mb-6">{scienceSection.introduction}</p>
                
                <div className="space-y-6">
                  {scienceSection.sections.map((section, i) => (
                    <div key={i} className="p-4 bg-background/50 rounded-lg">
                      <h3 className="font-medium text-foreground mb-2">{section.title}</h3>
                      <p className="text-sm text-foreground/70">{section.content}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Psychology Tab */}
          <TabsContent value="psychology" className="space-y-6">
            {/* Disclaimer Card */}
            <Card className="bg-primary/10 border-primary/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary">
                  <AlertTriangle className="h-5 w-5" />
                  {psychologicalFramework.disclaimer.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-foreground/80">{psychologicalFramework.disclaimer.content}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {psychologicalFramework.disclaimer.keyPoints.map((point, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 bg-background/50 rounded-lg">
                      <Shield className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-foreground/70">{point}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Introduction */}
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-primary" />
                  {psychologicalFramework.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80">{psychologicalFramework.introduction}</p>
              </CardContent>
            </Card>

            {/* Psychology Sections */}
            <div className="space-y-4">
              {psychologicalFramework.sections.map((section, i) => {
                const IconComponent = psyIconMap[section.icon] || Brain;
                return (
                  <Card key={i} className="bg-muted/30 border-muted/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <IconComponent className="h-5 w-5 text-accent" />
                        {section.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-foreground/80 text-sm">{section.content}</p>
                      <div className="p-3 bg-accent/10 rounded-lg">
                        <p className="text-xs text-muted-foreground italic">
                          <strong>Research Note:</strong> {section.researchNote}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Grounding Exercise */}
            <Card className="bg-gradient-to-br from-accent/10 to-primary/10 border-accent/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-accent" />
                  {psychologicalFramework.groundingExercise.title}
                </CardTitle>
                <CardDescription>{psychologicalFramework.groundingExercise.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ol className="space-y-3">
                  {psychologicalFramework.groundingExercise.steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center text-sm font-medium text-accent">
                        {i + 1}
                      </span>
                      <span className="text-foreground/80 text-sm pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
                <div className="p-3 bg-background/50 rounded-lg">
                  <p className="text-xs text-muted-foreground italic">{psychologicalFramework.groundingExercise.note}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Safety Tab */}
          <TabsContent value="safety" className="space-y-6">
            <Card className="bg-muted/30 border-muted/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-primary" />
                  {crystalSafetyGuide.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-foreground/80">{crystalSafetyGuide.introduction}</p>
                <div className="space-y-2">
                  {crystalSafetyGuide.generalSafety.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 bg-background/50 rounded-lg">
                      <Shield className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-foreground/70">{tip}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Toxic Crystals */}
            <Card className="bg-destructive/5 border-destructive/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-5 w-5" />
                  Crystals Requiring Special Care
                </CardTitle>
                <CardDescription>These crystals are safe when handled properly but require awareness</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {crystalSafetyGuide.toxicCrystals.map((crystal, i) => (
                    <div key={i} className="p-4 bg-background/50 rounded-lg border border-destructive/10">
                      <h4 className="font-medium text-foreground mb-2">{crystal.name}</h4>
                      <p className="text-sm text-destructive/80 mb-2">{crystal.warning}</p>
                      <p className="text-xs text-muted-foreground">
                        <strong>Safe use:</strong> {crystal.safeUse}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Water Safe & Sunlight Sensitive */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-accent/5 border-accent/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-accent">
                    <Droplets className="h-4 w-4" />
                    Water-Safe Crystals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {crystalSafetyGuide.waterSafeCrystals.map((crystal, i) => (
                      <Badge key={i} variant="secondary" className="bg-accent/10 text-accent">
                        {crystal}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-secondary/5 border-secondary/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-secondary">
                    <Sun className="h-4 w-4" />
                    Sunlight-Sensitive Crystals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {crystalSafetyGuide.sunlightSensitive.map((crystal, i) => (
                      <Badge key={i} variant="secondary" className="bg-secondary/10 text-secondary">
                        {crystal}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Ethical Sourcing */}
            <Card className="bg-secondary/5 border-secondary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-secondary">
                  <Heart className="h-5 w-5" />
                  {crystalSafetyGuide.ethicalSourcing.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-foreground/80">{crystalSafetyGuide.ethicalSourcing.content}</p>
                <div className="space-y-2">
                  {crystalSafetyGuide.ethicalSourcing.tips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 bg-background/50 rounded-lg">
                      <Sparkles className="h-4 w-4 text-secondary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-foreground/70">{tip}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Journal Tab */}
          <TabsContent value="journal" className="space-y-6">
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PenLine className="h-5 w-5 text-primary" />
                  {journalingPrompts.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/80">{journalingPrompts.introduction}</p>
              </CardContent>
            </Card>

            {/* Prompt Categories */}
            {journalingPrompts.categories.map((category, i) => (
              <Card key={i} className="bg-muted/30 border-muted/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{category.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {category.prompts.map((prompt, j) => (
                      <div key={j} className="p-3 bg-background/50 rounded-lg border-l-2 border-accent/50">
                        <p className="text-sm text-foreground/80 italic">{prompt}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Tracking Template */}
            <Card className="bg-accent/5 border-accent/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-accent" />
                  {journalingPrompts.trackingTemplate.title}
                </CardTitle>
                <CardDescription>Use this template to track your crystal sessions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {journalingPrompts.trackingTemplate.fields.map((field, i) => (
                    <div key={i} className="p-2 bg-background/50 rounded-lg text-center">
                      <span className="text-sm text-foreground/70">{field}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Crystal Detail Modal */}
      {selectedCrystal && renderCrystalDetail(selectedCrystal)}
    </div>
  );
};

export default RockRemedies;
